import React, { useEffect } from "react";

import { useMutation, useQuery } from "@apollo/client";
import { Box, Button, Typography } from "@mui/material";
import { NavLink, useNavigate } from "react-router-dom";

import { graphql } from "@/gql";
import { Recipe } from "@/gql/graphql";
import useAlert from "@/hooks/useAlert";
import useGlobalSnackbar from "@/hooks/useGlobalSnackbar";
import { displayError } from "@/utils/errors";

import RecipesList from "./components/RecipesList";

export const recipesPageQuery = graphql(`
  query RecipesPage {
    recipes {
      nodes {
        ...RecipeListItem
      }
    }
  }
`);

export const deleteRecipeMutation = graphql(`
  mutation DeleteRecipe($input: DeleteRecipeInput!) {
    deleteRecipe(input: $input) {
      recipe {
        id
      }
    }
  }
`);

const duplicateRecipeMutation = graphql(`
  mutation DuplicateRecipe($input: DuplicateRecipeInput!) {
    duplicateRecipe(input: $input) {
      recipe {
        id
      }
    }
  }
`);

const RecipesPage: React.FC = () => {
  const { loading, data } = useQuery(recipesPageQuery);
  const snackbar = useGlobalSnackbar();
  const navigate = useNavigate();
  const urlParams = new URLSearchParams(window.location.search);
  const queryParams = urlParams.get("duplicate") ?? "";
  const [deleteRecipe] = useMutation(deleteRecipeMutation);
  const [duplicateRecipe] = useMutation(duplicateRecipeMutation);
  const alert = useAlert();

  const handleDeleteRecipe = async (id: Recipe["id"]): Promise<void> => {
    try {
      const result = await deleteRecipe({
        variables: { input: { id: id } },
        refetchQueries: [recipesPageQuery],
      });
      if (result?.data?.deleteRecipe?.recipe?.id) {
        snackbar.open("Recipe has been deleted.");
      }
    } catch (e) {
      snackbar.open(
        displayError(
          e,
          "Something went wrong while deleting your recipe. Please try again later."
        )
      );
    }
  };

  const handleDuplicateRecipe = async (id: Recipe["id"]): Promise<void> => {
    try {
      await duplicateRecipe({
        variables: { input: { id: id } },
        refetchQueries: [recipesPageQuery],
      });
      snackbar.open("Recipe has been duplicated.");
    } catch (e) {
      snackbar.open(
        displayError(
          e,
          "Something went wrong while duplicating your recipe. Please try again later."
        )
      );
    }
  };

  useEffect(() => {
    if (queryParams != "") {
      alert.open(
        "Recipe successfully added!",
        "Your duplicate recipe has been successfully added"
      );
      navigate("/database/recipes");
    }
  }, [queryParams]);

  const recipeIds = (data?.recipes.nodes ?? []).map((r) => r.id);

  return (
    <>
      <Box
        display={"flex"}
        width={"100%"}
        alignItems={"center"}
        justifyContent={"space-between"}
        paddingY={2}
      >
        <Typography variant="body1" fontWeight="bold">
          Recipes
        </Typography>
        <Button
          variant="outlined"
          component={NavLink}
          to="/database/recipes/new"
        >
          Add new recipe
        </Button>
      </Box>
      <RecipesList
        isLoading={loading}
        recipeIds={recipeIds}
        onDeleteRecipe={handleDeleteRecipe}
        onDuplicateRecipe={handleDuplicateRecipe}
      />
    </>
  );
};

export default RecipesPage;
